import { useRef, useState } from "react";
import { ImagePlus, Loader2, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, IMAGE_LIBRARY, api, formatApiError } from "../../lib/api";

export const inputCls =
  "w-full bg-ink border border-white/10 px-3 py-2.5 text-sm text-silver placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-mauve";

const prettify = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function UploadButton({ onDone, testId = "upload-image-button" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd);
      onDone(`${BACKEND_URL}${res.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" className="hidden" onChange={pick} />
      <button
        type="button"
        data-testid={testId}
        disabled={busy}
        onClick={() => ref.current?.click()}
        className="shrink-0 inline-flex items-center gap-1.5 border border-mauve/40 text-mauve-light px-3 py-2.5 text-[10px] font-mono tracking-widest uppercase hover:bg-mauve hover:text-white transition-colors duration-200 disabled:opacity-40"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
      </button>
    </>
  );
}

function renderImageField(fieldKey, value, path, onChange) {
  return (
    <div data-testid={`field-${path}`} key={fieldKey}>
      <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 mb-2">{prettify(fieldKey)}</label>
      <div className="flex gap-3 items-start">
        {value && (
          <div className="w-24 h-16 shrink-0 overflow-hidden border border-white/10">
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex gap-2">
            <input value={value} onChange={(e) => onChange(path, e.target.value)} className={inputCls} placeholder="https://…" />
            <UploadButton testId={`upload-${path}`} onDone={(url) => onChange(path, url)} />
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer text-[10px] font-mono tracking-widest uppercase text-mauve flex items-center gap-1">
              <ImagePlus size={12} /> Pick from library
            </summary>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {IMAGE_LIBRARY.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  title={img.label}
                  data-testid={`imglib-${img.id}`}
                  onClick={() => onChange(path, img.url)}
                  className={`aspect-video overflow-hidden border transition-colors duration-200 ${
                    value === img.url ? "border-mauve" : "border-white/10 hover:border-mauve/50"
                  }`}
                >
                  <img src={img.url} alt={img.label} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

function renderTextField(fieldKey, value, path, onChange, onAi) {
  const long = value.length > 90;
  return (
    <div data-testid={`field-${path}`} key={fieldKey}>
      <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 mb-2">{prettify(fieldKey)}</label>
      <div className="flex gap-2 items-start">
        {long ? (
          <textarea value={value} rows={3} onChange={(e) => onChange(path, e.target.value)} className={`${inputCls} resize-y flex-1`} />
        ) : (
          <input value={value} onChange={(e) => onChange(path, e.target.value)} className={`${inputCls} flex-1`} />
        )}
        <button
          type="button"
          data-testid={`ai-btn-${path}`}
          onClick={() => onAi(path, value)}
          title="Rewrite with AI"
          className="shrink-0 p-2.5 border border-mauve/40 text-mauve hover:bg-mauve hover:text-white transition-colors duration-200"
        >
          <Wand2 size={15} />
        </button>
      </div>
    </div>
  );
}

function renderListField(fieldKey, value, path, onChange) {
  return (
    <div data-testid={`field-${path}`} key={fieldKey}>
      <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 mb-2">
        {prettify(fieldKey)} — one per line
      </label>
      <textarea
        value={value.join("\n")}
        rows={Math.min(value.length + 1, 8)}
        onChange={(e) => onChange(path, e.target.value.split("\n").filter((s) => s.trim() !== ""))}
        className={`${inputCls} resize-y`}
      />
    </div>
  );
}

function renderField(fieldKey, value, path, onChange, onAi) {
  if (typeof value === "string") {
    if (/image|photo|img|url/i.test(fieldKey)) return renderImageField(fieldKey, value, path, onChange);
    return renderTextField(fieldKey, value, path, onChange, onAi);
  }
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === "string")) return renderListField(fieldKey, value, path, onChange);
    return (
      <div className="space-y-4" key={fieldKey}>
        {value.map((item, i) => (
          <div key={`${path}.${i}`} className="border border-white/10 p-5 space-y-4 bg-ink/40">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-mauve">
              {prettify(fieldKey)} {i + 1}
            </p>
            {renderObjectFields(item, `${path}.${i}`, onChange, onAi)}
          </div>
        ))}
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <fieldset className="border border-white/10 p-5 space-y-4" key={fieldKey}>
        <legend className="font-mono text-[10px] tracking-[0.2em] uppercase text-mauve px-2">{prettify(fieldKey)}</legend>
        {renderObjectFields(value, path, onChange, onAi)}
      </fieldset>
    );
  }
  return null;
}

function renderObjectFields(data, basePath, onChange, onAi) {
  return Object.entries(data).map(([k, v]) =>
    renderField(k, v, basePath ? `${basePath}.${k}` : k, onChange, onAi)
  );
}

export function ObjectFields({ data, basePath, onChange, onAi }) {
  return <>{renderObjectFields(data, basePath, onChange, onAi)}</>;
}
